import { useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { Link } from "react-router-dom"

import { ErrorNotice, PrimaryButton } from "@features/portal/PortalPrimitives"
import { inboxApi, type InboxKind, type InboxRecord, type InboxStatus } from "@features/portal/inboxApi"
import PortalShell from "../Common/PortalShell"

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`

const FilterButton = styled.button<{ $active: boolean }>`
  min-height: 42px;
  padding: 0 1rem;
  border: 1px solid ${({ $active }) => ($active ? "#153f63" : "#b8c8d5")};
  border-radius: 2px;
  background: ${({ $active }) => ($active ? "#153f63" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#24445f")};
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
`

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  margin-bottom: 1.25rem;
  border: 1px solid #c4d0da;
  background: #c4d0da;

  div {
    padding: 1.1rem 1.25rem;
    background: #ffffff;
  }

  strong {
    display: block;
    color: #0c3151;
    font-size: 1.7rem;
    line-height: 1;
  }

  span {
    display: block;
    margin-top: 0.35rem;
    color: #647a8c;
    font-size: 0.76rem;
  }
`

const InboxGrid = styled.div`
  display: grid;
  gap: 0.75rem;
`

const Card = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.5fr);
  border: 1px solid #c4d0da;
  border-radius: 2px;
  background: #ffffff;
  box-shadow: 0 16px 38px rgba(8, 36, 60, 0.05);

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const CardMain = styled.div`
  min-width: 0;
  padding: clamp(1.25rem, 3vw, 2rem);
`

const CardAside = styled.div`
  padding: clamp(1.25rem, 3vw, 2rem);
  border-left: 1px solid #d1dbe3;
  background: #f5f8fa;

  @media (max-width: 800px) {
    border-top: 1px solid #d1dbe3;
    border-left: 0;
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`

const Kind = styled.span`
  display: inline-flex;
  padding: 0.35rem 0.55rem;
  border: 1px solid #9fc2dc;
  color: #1c659b;
  font-size: 0.65rem;
  font-weight: 850;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`

const DateText = styled.time`
  color: #718595;
  font-size: 0.76rem;
  white-space: nowrap;
`

const Name = styled.h2`
  margin: 0;
  color: #0b2c4a;
  font-size: clamp(1.35rem, 3vw, 2rem);
  font-weight: 620;
  letter-spacing: -0.035em;
`

const Details = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem 1.5rem;
  margin: 1.25rem 0 0;

  div:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  dt {
    margin-bottom: 0.2rem;
    color: #748797;
    font-size: 0.67rem;
    font-weight: 750;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  dd {
    margin: 0;
    color: #263e52;
    line-height: 1.55;
  }

  a {
    color: #1c659b;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const FieldLabel = styled.label`
  display: grid;
  gap: 0.45rem;
  margin-bottom: 0.9rem;
  color: #2a465e;
  font-size: 0.75rem;
  font-weight: 750;

  select,
  textarea {
    width: 100%;
    border: 1px solid #aebfcd;
    border-radius: 2px;
    background: #ffffff;
    color: #173750;
    font: inherit;
  }

  select {
    min-height: 44px;
    padding: 0 0.75rem;
  }

  textarea {
    min-height: 104px;
    resize: vertical;
    padding: 0.75rem;
    line-height: 1.5;
  }
`

const Saved = styled.span`
  margin-left: 0.75rem;
  color: #297457;
  font-size: 0.75rem;
  font-weight: 700;
`

const Empty = styled.div`
  padding: 3rem 1.5rem;
  border: 1px solid #c4d0da;
  background: #ffffff;
  color: #657b8e;
  text-align: center;
`

const ContextLink = styled(Link)`
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  padding: 0 1rem;
  border: 1px solid #aebfcd;
  border-radius: 2px;
  color: #173f62;
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 750;
`

const kindLabel: Record<InboxKind, string> = {
  CONSULTATION: "Consultation propriétaire",
  CONTACT: "Contact général",
  RAPPEL: "Demande de rappel",
}

const statusLabel: Record<InboxStatus, string> = {
  NOUVELLE: "Nouvelle",
  EN_COURS: "En cours",
  TRAITEE: "Traitée",
}

const formatDate = (date?: string) => {
  if (!date) return "Date non disponible"
  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(date))
}

const formatPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, "")
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : phone
}

const recordName = (record: InboxRecord) => (record.kind === "RAPPEL" ? `Rappel — ${record.sector}` : `${record.firstName} ${record.lastName}`)

const RecordDetails = ({ record }: { record: InboxRecord }) => {
  if (record.kind === "CONSULTATION") {
    return (
      <Details>
        <div>
          <dt>Courriel</dt>
          <dd>
            <a href={`mailto:${record.email}`}>{record.email}</a>
          </dd>
        </div>
        <div>
          <dt>Téléphone</dt>
          <dd>
            <a href={`tel:${record.phone}`}>{formatPhone(record.phone)}</a>
          </dd>
        </div>
        <div>
          <dt>Secteur</dt>
          <dd>{record.sector}</dd>
        </div>
        <div>
          <dt>Portefeuille</dt>
          <dd>
            {record.numBuildings} immeuble{record.numBuildings === 1 ? "" : "s"} · {record.numUnits} porte{record.numUnits === 1 ? "" : "s"}
          </dd>
        </div>
        {record.legalEntity && (
          <div>
            <dt>Entreprise</dt>
            <dd>{record.legalEntity}</dd>
          </div>
        )}
        {record.additionalInfo && (
          <div>
            <dt>Précisions</dt>
            <dd>{record.additionalInfo}</dd>
          </div>
        )}
      </Details>
    )
  }

  if (record.kind === "CONTACT") {
    return (
      <Details>
        <div>
          <dt>Courriel</dt>
          <dd>
            <a href={`mailto:${record.email}`}>{record.email}</a>
          </dd>
        </div>
        <div>
          <dt>Téléphone</dt>
          <dd>
            <a href={`tel:${record.phone}`}>{formatPhone(record.phone)}</a>
          </dd>
        </div>
        <div>
          <dt>Type</dt>
          <dd>{record.inquiryType}</dd>
        </div>
        <div>
          <dt>Message</dt>
          <dd>{record.message}</dd>
        </div>
      </Details>
    )
  }

  return (
    <Details>
      <div>
        <dt>Téléphone</dt>
        <dd>
          <a href={`tel:${record.phone}`}>{formatPhone(record.phone)}</a>
        </dd>
      </div>
      <div>
        <dt>Secteur</dt>
        <dd>{record.sector}</dd>
      </div>
      {record.unitRange && (
        <div>
          <dt>Nombre de logements</dt>
          <dd>{record.unitRange}</dd>
        </div>
      )}
      {record.mainIssue && (
        <div>
          <dt>Enjeu principal</dt>
          <dd>{record.mainIssue}</dd>
        </div>
      )}
    </Details>
  )
}

const InboxCard = ({ record, onUpdated }: { record: InboxRecord; onUpdated: (record: InboxRecord) => void }) => {
  const [status, setStatus] = useState<InboxStatus>(record.status || "NOUVELLE")
  const [notes, setNotes] = useState(record.notes || "")
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState("")

  const save = async () => {
    setSaving(true)
    setSaved(false)
    setError("")
    try {
      const updated = await inboxApi.update(record.kind, record._id, { status, notes })
      onUpdated({
        ...record,
        status: updated.status || status,
        notes: updated.notes ?? notes,
        updatedAt: updated.updatedAt || record.updatedAt,
      })
      setSaved(true)
    } catch {
      setError("La mise à jour n’a pas pu être enregistrée.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardMain>
        <CardHeader>
          <Kind>{kindLabel[record.kind]}</Kind>
          <DateText dateTime={record.createdAt}>{formatDate(record.createdAt)}</DateText>
        </CardHeader>
        <Name>{recordName(record)}</Name>
        <RecordDetails record={record} />
      </CardMain>
      <CardAside>
        <FieldLabel>
          État du suivi
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as InboxStatus)
              setSaved(false)
            }}
          >
            {Object.entries(statusLabel).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </FieldLabel>
        <FieldLabel>
          Notes internes
          <textarea
            value={notes}
            maxLength={2000}
            onChange={(event) => {
              setNotes(event.target.value)
              setSaved(false)
            }}
            placeholder="Prochaine action, rappel effectué, détails utiles…"
          />
        </FieldLabel>
        <PrimaryButton type="button" onClick={save} disabled={saving}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </PrimaryButton>
        {saved && <Saved>Enregistré</Saved>}
        {error && <ErrorNotice>{error}</ErrorNotice>}
      </CardAside>
    </Card>
  )
}

type Filter = "TOUTES" | InboxKind | InboxStatus

const Inbox = () => {
  const [records, setRecords] = useState<InboxRecord[]>([])
  const [filter, setFilter] = useState<Filter>("TOUTES")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let active = true
    inboxApi
      .list()
      .then((items) => active && setRecords(items))
      .catch(() => active && setError("La réception ne peut pas être chargée pour le moment."))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  const counts = useMemo(
    () => ({
      new: records.filter((record) => (record.status || "NOUVELLE") === "NOUVELLE").length,
      active: records.filter((record) => record.status === "EN_COURS").length,
      done: records.filter((record) => record.status === "TRAITEE").length,
    }),
    [records],
  )

  const visibleRecords = records.filter((record) => {
    if (filter === "TOUTES") return true
    if (["CONSULTATION", "CONTACT", "RAPPEL"].includes(filter)) return record.kind === filter
    return (record.status || "NOUVELLE") === filter
  })

  const updateRecord = (updated: InboxRecord) => {
    setRecords((current) => current.map((record) => (record._id === updated._id && record.kind === updated.kind ? updated : record)))
  }

  return (
    <PortalShell
      eyebrow="Administration"
      title="Réception"
      action={<ContextLink to="/app/gestion-des-candidatures">Voir les candidatures locatives</ContextLink>}
    >
      <Summary>
        <div>
          <strong>{loading ? "—" : counts.new}</strong>
          <span>Nouvelles</span>
        </div>
        <div>
          <strong>{loading ? "—" : counts.active}</strong>
          <span>En cours</span>
        </div>
        <div>
          <strong>{loading ? "—" : counts.done}</strong>
          <span>Traitées</span>
        </div>
      </Summary>

      <Filters aria-label="Filtrer la réception">
        {(
          [
            ["TOUTES", "Toutes"],
            ["NOUVELLE", "Nouvelles"],
            ["EN_COURS", "En cours"],
            ["CONSULTATION", "Consultations"],
            ["CONTACT", "Contacts"],
            ["RAPPEL", "Rappels"],
            ["TRAITEE", "Traitées"],
          ] as [Filter, string][]
        ).map(([value, label]) => (
          <FilterButton key={value} type="button" $active={filter === value} onClick={() => setFilter(value)}>
            {label}
          </FilterButton>
        ))}
      </Filters>

      {error && <ErrorNotice>{error}</ErrorNotice>}
      {loading ? (
        <Empty>Chargement de la réception…</Empty>
      ) : visibleRecords.length ? (
        <InboxGrid>
          {visibleRecords.map((record) => (
            <InboxCard key={`${record.kind}-${record._id}`} record={record} onUpdated={updateRecord} />
          ))}
        </InboxGrid>
      ) : (
        <Empty>Aucune demande dans cette vue.</Empty>
      )}
    </PortalShell>
  )
}

export default Inbox
